import { useQuery } from "@tanstack/react-query";
import Api from "src/Config";
import {
  createContext,
  FunctionComponent,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";

export const AccountTrailContext = createContext(null);

type Props = {
  children: ReactElement;
};

const AccountTrailProvider: FunctionComponent<Props> = ({ children }) => {
  const { id } = useParams();
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["view_account_trail"],
    queryFn: async () => {
      const { data } = await Api.get(
        `backoffice/onboarding/risk-profiles/${id}/activity-log`,
      );

      const response = data.results.map(
        (res: {
          message: string;
          created_at: string;
          user: {
            id: string;
            first_name: string;
            last_name: string;
          };
        }) => ({
          id: res.user.id,
          "activity by": `${
            !res.user.first_name?.toLowerCase() &&
            !res.user.last_name?.toLowerCase()
              ? "N/A"
              : `${res.user.first_name?.toLowerCase() || ""}  ${
                  res.user.last_name?.toLowerCase() || ""
                }`
          }`,
          "date and time": format(new Date(res.created_at), "dd/MM/yyyy"),
          "activity done": res.message || "N/A",
        }),
      );

      return { response, metadata: data?.metadata };
    },
  });

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.metadata.count / 10));
    }
  }, [data]);

  const contextValue = useMemo(
    () => ({
      data,
      isLoading,
      isFetching,
      pageCount,
      pageNumber,
      setPageNumber,
    }),
    [isLoading, data, error, isFetching, pageCount, pageNumber, setPageNumber],
  );
  return (
    <AccountTrailContext.Provider value={contextValue}>
      {children}
    </AccountTrailContext.Provider>
  );
};

export default AccountTrailProvider;
